import React, { useMemo, useState } from 'react'

import {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
  NextPage,
} from 'next'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { Status, Wrapper } from '@googlemaps/react-wrapper'
import { useErrorBoundary } from 'react-error-boundary'
import { useInterval } from 'react-use'

import { isSuperFetchError } from '~/api-client'
import { useFoodMutation, useFoodSWR } from '~/api-client/food_api'
import {
  restaurantGetRestaurant,
  restaurantRestaurantPhotoClips,
} from '~/api-client/food_api/restaurant'
import { Image as RestaurantImage } from '~/api-definition/definition'
import { Button } from '~/components/action/Button'
import { Modal } from '~/components/action/Modal'
import { AppImage } from '~/components/data-display/AppImage'
import { Heading } from '~/components/data-display/Heading'
import { InfoList } from '~/components/data-display/InfoList'
import {
  ImageModal,
  ImageModalUser,
  formatVisitedDate,
} from '~/components/food_app/restaurant/ImageModal'
import { Map, Marker } from '~/components/food_app/restaurant/Map'
import { UserWarningStatusIconButton } from '~/components/food_app/user/UserWarningStatus'
import {
  CameraIcon,
  ChevronLeftIcon,
  FacebookIcon,
  HomeIcon,
  InstagramIcon,
  MapIcon,
  TabelogIcon,
  TwitterIcon,
} from '~/components/icons'
import { Container, ContainerInset } from '~/components/layout/Container'
import { Footer } from '~/components/layout/Footer'
import { Header } from '~/components/layout/Header'
import { config } from '~/config'
import { restaurantAreaAndGenre } from '~/libs/restaurant'
import { userAttributesLabel, userProfileIcon } from '~/libs/user'

type RestaurantLinkProps = {
  icon: React.FC<any>
  url: string
  iconClassName?: string
}

const RestaurantLink = ({
  icon: Icon,
  url,
  iconClassName,
}: RestaurantLinkProps) => {
  return (
    <Link
      className="flex items-start pb-2 gap-x-2"
      href={url}
      target="_blank"
      rel="noopener"
    >
      <Icon className={`mt-1 w-6 flex-shrink-0 ${iconClassName}`} />
      <div className="text-[12px] text-[#999] whitespace-normal break-all">
        {url}
      </div>
    </Link>
  )
}

const Page: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = (
  props
) => {
  const router = useRouter()
  const { id } = router.query
  const [imageModalIndex, setImageModalIndex] = useState<number | null>(null)

  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(
    null
  )

  const isPhoneNumber = (value: string): boolean => {
    return /^[0-9]+-[0-9]+-[0-9]+$/.test(value)
  }

  const restaurantUrls = () => {
    if (
      !props.restaurant.officialUrl &&
      !props.restaurant.facebookUrl &&
      !props.restaurant.instagramUrl &&
      !props.restaurant.tabelogRestaurantUrl &&
      !props.restaurant.twitterUrl
    )
      return null
    return (
      <>
        {props.restaurant.officialUrl &&
          RestaurantLink({
            icon: HomeIcon,
            url: props.restaurant.officialUrl,
          })}

        {props.restaurant.facebookUrl &&
          RestaurantLink({
            icon: FacebookIcon,
            url: props.restaurant.facebookUrl,
            iconClassName: 'fill-[#999]',
          })}

        {props.restaurant.instagramUrl &&
          RestaurantLink({
            icon: InstagramIcon,
            url: props.restaurant.instagramUrl,
            iconClassName: 'fill-[#999]',
          })}
        {props.restaurant.tabelogRestaurantUrl &&
          RestaurantLink({
            icon: TabelogIcon,
            url: props.restaurant.tabelogRestaurantUrl,
            iconClassName: 'fill-[#999]',
          })}
        {props.restaurant.twitterUrl &&
          RestaurantLink({
            icon: TwitterIcon,
            url: props.restaurant.twitterUrl,
            iconClassName: 'fill-[#999]',
          })}
      </>
    )
  }

  useInterval(
    () => {
      if (center) {
        return
      }
      if (props.restaurant.latitude && props.restaurant.longitude) {
        setCenter({
          lat: Number(props.restaurant.latitude),
          lng: Number(props.restaurant.longitude),
        })
        return
      }
    },
    center ? null : 1000
  )

  const infoList = [
    {
      title: '予約・\nお問い合わせ',
      value: isPhoneNumber(props.restaurant.phoneNumber) ? (
        <Link href={'tel:' + props.restaurant.phoneNumber}>
          <Button variant="functionWeak">{props.restaurant.phoneNumber}</Button>
        </Link>
      ) : (
        '不明のため情報お待ちしております'
      ),
    },
    { title: '予約可否', value: props.restaurant.reservation },
    { title: '営業時間', value: props.restaurant.businessHour },
    { title: '予算(昼)', value: props.restaurant.budgetLunch },
    { title: '予算(夜)', value: props.restaurant.budgetDinner },
    { title: '交通手段', value: props.restaurant.transportation },
    { title: '支払い方法', value: props.restaurant.payment },
    { title: '席数', value: props.restaurant.seat },
    { title: '個室', value: props.restaurant.privateRoom },
    { title: '禁煙・喫煙', value: props.restaurant.smoking },
    { title: '駐車場', value: props.restaurant.parking },
    { title: 'お子様連れ', value: props.restaurant.kids },
    { title: '電話番号', value: props.restaurant.phoneNumber },
    { title: '開店日', value: props.restaurant.openedDate },
    {
      title: '公式URL',
      value: restaurantUrls(),
    },
    { title: '備考', value: props.restaurant.remark },
  ].filter((item) => item.value && item.value !== '')

  return (
    <>
      <Head>
        <title>{props.restaurant.restaurantName}</title>
      </Head>
      <Header>
        <ChevronLeftIcon fill="#fff" onClick={() => router.back()} />
        <div />
        {props.restaurant.restaurantName}
        <div />
        {props.restaurant.latitude != null &&
        props.restaurant.latitude.length > 0 &&
        props.restaurant.longitude != null &&
        props.restaurant.longitude.length > 0 ? (
          <Link href={`/restaurants/${props.restaurant.restaurantId}/map`}>
            <MapIcon fill="#fff" />
          </Link>
        ) : (
          <div></div>
        )}
      </Header>

      <Container>
        <div className="relative w-full pb-[56%] bg-empty-image">
          {props.restaurant.coverImages.length > 0 && (
            <Image
              unoptimized
              fill
              alt="店舗画像"
              src={
                props.restaurant.coverImages[0]?.imageUrl ??
                config.noImageUrl.restaurantCoverImage
              }
              className="object-cover"
            />
          )}

          <div className="absolute bottom-0 left-0 text-white font-bold">
            <div className="px-4 py-2">
              <div className="text-lg">{props.restaurant.restaurantName}</div>
              <div className="text-sm">
                {restaurantAreaAndGenre(props.restaurant)}
              </div>
            </div>
          </div>
        </div>

        <Heading variant="h2" component="h2">
          投稿写真
        </Heading>

        <ContainerInset>
          {props.photoClips.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {props.images.map((image, i) => {
                return (
                  <div className="relative w-full pb-[100%]" key={i}>
                    <Image
                      unoptimized
                      alt={`投稿画像${i + 1}`}
                      fill
                      className="object-cover rounded"
                      src={image.imageUrl}
                      onClick={() => setImageModalIndex(i)}
                    />
                    {i === props.images.length - 1 &&
                      props.images.length > 0 && (
                        <Link href={`/restaurants/${id}/images`}>
                          <div className="bg-black absolute top-0 left-0 w-full h-full opacity-80">
                            <div className="rounded-md mx-auto flex items-center h-full">
                              <div className="mx-auto">
                                <CameraIcon className="fill-white w-8 h-8 mx-auto" />
                                <p className="text-white text-xs text-center">
                                  {props.allImageCount}
                                </p>
                              </div>
                            </div>
                          </div>
                        </Link>
                      )}
                  </div>
                )
              })}
            </div>
          )}
          {props.photoClips.length === 0 && (
            <div className="text-center text-black text-sm">
              <p>投稿写真がありません</p>
              <p>一番乗りで投稿してみませんか？</p>

              <label>
                <Link href={`/restaurants/${id}/add_images`}>
                  <div className="bg-[#E7E7E7] w-20 h-20 rounded-md mx-auto flex justify-center items-center mt-6">
                    <div className="">
                      <CameraIcon className="fill-[#AEAEAE] w-6 h-6 mx-auto" />
                      <p className="text-[#AEAEAE] text-xs">写真を追加</p>
                    </div>
                  </div>
                </Link>
              </label>
            </div>
          )}

          {imageModalIndex !== null && (
            <ImageModal
              thumbnailImages={props.images.map((e) => e.imageUrl)}
              isOpen={imageModalIndex !== null}
              imageIndex={imageModalIndex}
              onClose={() => setImageModalIndex(null)}
              users={props.users}
              visitedDates={props.visitedDates}
            />
          )}
        </ContainerInset>

        <Heading variant="h2" component="h2">
          行きたいお店に登録しているフーディー
        </Heading>

        <ContainerInset>
          <RestaurantFollowingUsers
            restaurantId={props.restaurant.restaurantId}
          />
        </ContainerInset>

        <Heading variant="h2" component="h2">
          住所
        </Heading>

        <ContainerInset>
          {!props.restaurant.address && (
            <div className="text-center text-black text-sm">
              <p>住所情報はありません</p>
            </div>
          )}

          <div className="pb-4">{props.restaurant.address}</div>
          {props.restaurant.latitude != null &&
            props.restaurant.latitude.length > 0 &&
            props.restaurant.longitude != null &&
            props.restaurant.longitude.length > 0 && (
              <div className="relative w-full h-96">
                <div style={{ display: 'flex', height: '100%' }}>
                  <Wrapper
                    apiKey={config.googleMap.apiKey}
                    render={(status: Status) => {
                      return <h1>{status}</h1>
                    }}
                  >
                    <Map
                      center={center}
                      zoom={14}
                      style={{ flexGrow: '1', height: '100%' }}
                    >
                      <Marker key={'marker'} position={center} />
                    </Map>
                  </Wrapper>
                </div>
              </div>
            )}
        </ContainerInset>

        <Heading variant="h2" component="h2">
          店舗情報
        </Heading>

        <ContainerInset>
          {infoList.length == 0 && (
            <>
              <div className="text-center text-black text-sm">
                <p>店舗情報はありません</p>
              </div>
            </>
          )}
          {infoList.length > 0 && <InfoList data={infoList} />}
        </ContainerInset>

        {/* TODO: v2.0.0以降に実装
        <ContainerInset>
          <div className="flex items-center justify-center gap-4">
            <Button variant="functionWeak">誤りを報告する</Button>
            <Button variant="function">店主申請をする</Button>
          </div>
        </ContainerInset>
          */}
      </Container>
      <Footer />
    </>
  )
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  try {
    let restaurantId = context.query.id as string

    // MEMO: 運営側の作業用途のために、隠しパラメータ referral_url がある場合は優先する（clips結合のために事前リクエスト）
    if (typeof context.query.referral_url === 'string') {
      const { restaurant } = await restaurantGetRestaurant({
        searchParams: { referral_restaurant_url: context.query.referral_url },
      })
      restaurantId = restaurant.restaurantId
    }

    const [restaurantResponse, photoClipsResponse] = await Promise.all([
      restaurantGetRestaurant({
        searchParams: { restaurant_id: restaurantId },
      }),
      restaurantRestaurantPhotoClips({
        searchParams: { restaurant_id: restaurantId },
      }),
    ])

    let images: RestaurantImage[] = []
    let users: ImageModalUser[] = []
    let visitedDates: (string | undefined)[] = []
    photoClipsResponse.photoClips.forEach((photoClip) => {
      images = images.concat(photoClip.images)
      photoClip.images.forEach((_) => {
        users = users.concat({
          nickname: photoClip.user?.displayName,
          userName: photoClip.user?.userName,
          attribute: photoClip.user
            ? userAttributesLabel(photoClip.user)
            : undefined,
          imageUrl: photoClip.user?.profileIcon?.imageUrl,
        })
        visitedDates = visitedDates.concat(
          formatVisitedDate(photoClip.visitedDate)
        )
      })
    })
    const props = {
      restaurant: restaurantResponse.restaurant,
      photoClips: photoClipsResponse.photoClips,
      images: images.splice(0, 6),
      users: users.splice(0, 6),
      visitedDates: visitedDates.splice(0, 6),
      allImageCount: photoClipsResponse.allImageCount,
    }
    return { props }
  } catch (error) {
    // id の format error で 400 が返ってくる場合もハンドリングする
    if (
      isSuperFetchError(error) &&
      (error.response.status === 404 || error.response.status === 400)
    ) {
      return {
        redirect: {
          destination: '/dinners',
          permanent: false,
        },
      }
    }
    throw error
  }
}

const RestaurantFollowingUsers: React.FC<{ restaurantId: string }> = (
  props
) => {
  const router = useRouter()
  const { showBoundary } = useErrorBoundary()
  const [reachedToLimit, setReachedToLimit] = useState(false)
  const [created, setCreated] = useState(false)
  const [deleted, setDeleted] = useState(false)

  const displayUserLimit = 7
  const restaurantFollowingUsers = useFoodSWR(
    '/user/restaurant_following_users',
    {
      restaurantId: props.restaurantId,
      followingType: 'wish',
      count: displayUserLimit,
    }
  )
  const followingRestaurantList = useFoodSWR(
    '/user/restaurant_following_list',
    {},
    { overrideAuthType: 'required' }
  )

  const newFollowingWishRestaurantList = useFoodMutation(
    '/user/new_following_wish_restaurant'
  )
  const deleteFollowingWishRestaurantList = useFoodMutation(
    '/user/delete_following_wish_restaurant'
  )

  const users = useMemo(() => {
    return restaurantFollowingUsers.data?.users
  }, [restaurantFollowingUsers.data?.users])
  const omitDisplayUserCount = useMemo(() => {
    return (
      restaurantFollowingUsers.data &&
      restaurantFollowingUsers.data.paging.all - displayUserLimit
    )
  }, [restaurantFollowingUsers.data])

  return (
    <div>
      <Modal
        isOpen={created}
        setIsOpen={async (v) => {
          if (!v) {
            await followingRestaurantList.mutate()
            setCreated(false)
          }
        }}
        title="登録が完了しました。"
      />
      <Modal
        isOpen={deleted}
        setIsOpen={async (v) => {
          if (!v) {
            await followingRestaurantList.mutate()
            setDeleted(false)
          }
        }}
        title="削除しました"
      />
      <Modal
        isOpen={reachedToLimit}
        title="行きたいお店が9つ登録済です"
        actionButtons={
          <Button
            type="button"
            variant="functionWeak"
            className="w-full"
            onClick={() => router.push('/account/following_restaurants/wish')}
          >
            OK
          </Button>
        }
      />
      <div className="flex flex-wrap items-start gap-3">
        {users?.length === 0 && (
          <div className="text-center text-black text-sm">
            <p>登録がありません</p>
          </div>
        )}
        {users?.map((user) => (
          <div
            key={user.userId}
            className="min-w-[80px] flex flex-col items-center gap-y-2"
          >
            <Link href={`/foodies/${user.userName}`}>
              <AppImage
                variant={{ type: 'profile', user: user }}
                rounded="full"
                width={75}
                height={75}
                className="rounded-full"
                alt={`${user.displayName}さんのプロフィール画像`}
                src={userProfileIcon(user)}
              />
            </Link>
            <div className="text-xs text-center max-w-[75px] break-words h-9 flex items-center justify-center flex-wrap gap-0.5">
              {user.displayName}
              {user.warningStatus != null && (
                <UserWarningStatusIconButton
                  userWarningStatus={user.warningStatus}
                />
              )}
            </div>
          </div>
        ))}

        {omitDisplayUserCount && omitDisplayUserCount > 0 ? (
          <Link
            href={`/restaurants/${props.restaurantId}/following_users/wish`}
          >
            <p className="text-xs w-[75px] h-[75px] flex justify-center items-center bg-[#E6E6EA] rounded-full">
              他{omitDisplayUserCount}名
            </p>
          </Link>
        ) : (
          <></>
        )}
      </div>
      {followingRestaurantList.data && (
        <div className="flex justify-center mt-5">
          {followingRestaurantList.data.followingList.wishRestaurants.filter(
            (r) => r.restaurantId === props.restaurantId
          ).length === 0 ? (
            <Button
              variant="primary"
              type="button"
              className="px-5"
              onClick={async () => {
                if (followingRestaurantList.data) {
                  if (
                    followingRestaurantList.data.followingList.wishRestaurants.filter(
                      (r) => r.restaurantId != null
                    ).length >=
                    followingRestaurantList.data.followingList
                      .wishRestaurantLimit
                  ) {
                    setReachedToLimit(true)
                  } else {
                    try {
                      await newFollowingWishRestaurantList({
                        restaurantId: props.restaurantId,
                      })
                      setCreated(true)
                    } catch (e) {
                      showBoundary(e)
                    }
                  }
                }
              }}
            >
              行きたいお店に追加する
            </Button>
          ) : (
            <Button
              variant="functionWeak"
              type="button"
              className="px-5"
              onClick={async () => {
                try {
                  await deleteFollowingWishRestaurantList({
                    restaurantId: props.restaurantId,
                  })
                  setDeleted(true)
                } catch (e) {
                  showBoundary(e)
                }
              }}
            >
              行きたいお店から削除する
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default Page
